import React, { useState } from 'react';
import { Card, Col, Row, Select, Statistic, Table, Tabs, Typography, Button } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { quotationConsolidationService } from '@/services/quotationConsolidation.service';
import salesConversionReportsService from '@/services/salesConversionReports.service';
import { useCurrency } from '@/contexts/CurrencyContext';
import { defaultSalesReportFilters, SalesReportFilterBar } from './SalesReportFilterBar';

const { Title } = Typography;

const QuotationConversionReportsPage: React.FC = () => {
  const navigate = useNavigate();
  const { formatCurrency } = useCurrency();
  const [status, setStatus] = useState('Partially Converted');
  const [filters, setFilters] = useState(defaultSalesReportFilters());

  const { data: register = [], isLoading: regLoading } = useQuery({
    queryKey: ['quotation-conversion-register'],
    queryFn: () => quotationConsolidationService.getConversionRegister(),
  });

  const { data: byStatus = [], isLoading: stLoading } = useQuery({
    queryKey: ['quotation-conversion-by-status', status],
    queryFn: () => quotationConsolidationService.getQuotationsByStatus(status),
  });

  const { data: analytics, isLoading: anLoading } = useQuery({
    queryKey: ['quotation-conversion-analytics', filters],
    queryFn: () => salesConversionReportsService.quotationConversionAnalytics(filters),
  });

  const kpis = (analytics?.kpis as Record<string, unknown>) || {};
  const breakdowns = (analytics?.breakdowns as Record<string, Array<Record<string, unknown>>>) || {};

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>Quotation Conversion</Title>
      <Tabs
        items={[
          {
            key: 'analytics',
            label: 'Analytics',
            children: (
              <>
                <SalesReportFilterBar value={filters} onChange={setFilters} showCompare={false} />
                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                  <Col xs={12} md={6}>
                    <Card loading={anLoading}>
                      <Statistic title="Win rate" value={kpis.win_rate_pct == null ? '—' : `${kpis.win_rate_pct}%`} />
                    </Card>
                  </Col>
                  <Col xs={12} md={6}>
                    <Card loading={anLoading}>
                      <Statistic title="Converted to SO" value={Number(kpis.converted_to_so || 0)} />
                    </Card>
                  </Col>
                  <Col xs={12} md={6}>
                    <Card loading={anLoading}>
                      <Statistic
                        title="Conv % (count)"
                        value={
                          kpis.conversion_pct_by_count == null ? '—' : `${kpis.conversion_pct_by_count}%`
                        }
                      />
                    </Card>
                  </Col>
                  <Col xs={12} md={6}>
                    <Card loading={anLoading}>
                      <Statistic
                        title="Avg days to SO"
                        value={kpis.avg_days_to_so == null ? '—' : Number(kpis.avg_days_to_so)}
                      />
                    </Card>
                  </Col>
                </Row>
                <Card title="Open quote aging" style={{ marginBottom: 16 }}>
                  <Table
                    loading={anLoading}
                    size="small"
                    rowKey={(r) => String(r.bucket)}
                    dataSource={breakdowns.aging_open || []}
                    pagination={false}
                    columns={[
                      { title: 'Bucket', dataIndex: 'bucket' },
                      { title: 'Count', dataIndex: 'count', align: 'right' },
                      {
                        title: 'Value',
                        dataIndex: 'value',
                        align: 'right',
                        render: (v) => formatCurrency(Number(v || 0)),
                      },
                    ]}
                  />
                </Card>
                <Card title="By salesperson" style={{ marginBottom: 16 }}>
                  <Table
                    loading={anLoading}
                    size="small"
                    rowKey={(r) => String(r.owner_id || r.salesperson)}
                    dataSource={breakdowns.by_salesperson || []}
                    columns={[
                      { title: 'Salesperson', dataIndex: 'salesperson' },
                      { title: 'Quotes', dataIndex: 'count', align: 'right' },
                      { title: 'Accepted', dataIndex: 'accepted', align: 'right' },
                      {
                        title: 'Value',
                        dataIndex: 'value',
                        align: 'right',
                        render: (v) => formatCurrency(Number(v || 0)),
                      },
                    ]}
                    pagination={{ pageSize: 10 }}
                  />
                </Card>
                <Card title="Top customers">
                  <Table
                    loading={anLoading}
                    size="small"
                    rowKey={(r) => String(r.customer_name)}
                    dataSource={breakdowns.by_customer || []}
                    columns={[
                      { title: 'Customer', dataIndex: 'customer_name' },
                      { title: 'Quotes', dataIndex: 'count', align: 'right' },
                      {
                        title: 'Value',
                        dataIndex: 'value',
                        align: 'right',
                        render: (v) => formatCurrency(Number(v || 0)),
                      },
                    ]}
                    pagination={false}
                  />
                </Card>
              </>
            ),
          },
          {
            key: 'register',
            label: 'Conversion register',
            children: (
              <Card>
                <Table
                  loading={regLoading}
                  rowKey="id"
                  size="small"
                  dataSource={register as Record<string, unknown>[]}
                  columns={[
                    {
                      title: 'Date',
                      dataIndex: 'created_at',
                      render: (v) => (v ? dayjs(String(v)).format('DD-MMM-YYYY HH:mm') : '—'),
                    },
                    { title: 'Customer order', dataIndex: 'sales_order_number' },
                    { title: 'Quotation', dataIndex: 'quotation_number' },
                    { title: 'Line', dataIndex: 'line_number' },
                    { title: 'Description', dataIndex: 'item_description', ellipsis: true },
                    { title: 'Qty', dataIndex: 'converted_qty', align: 'right' },
                  ]}
                />
              </Card>
            ),
          },
          {
            key: 'status',
            label: 'By status',
            children: (
              <Card>
                <Select
                  style={{ width: 220, marginBottom: 16 }}
                  value={status}
                  onChange={setStatus}
                  options={[
                    { value: 'Partially Converted', label: 'Partially converted' },
                    { value: 'Fully Converted', label: 'Fully converted' },
                  ]}
                />
                <Table
                  loading={stLoading}
                  rowKey="id"
                  size="small"
                  dataSource={byStatus as Array<Record<string, unknown>>}
                  columns={[
                    { title: 'Quotation', dataIndex: 'quotation_number' },
                    { title: 'Customer', dataIndex: 'customer_name' },
                    { title: 'Status', dataIndex: 'status' },
                    {
                      title: 'Value',
                      dataIndex: 'grand_total',
                      align: 'right',
                      render: (v) => formatCurrency(Number(v)),
                    },
                    {
                      title: '',
                      key: 'act',
                      render: (_, r) => (
                        <Button
                          type="link"
                          size="small"
                          onClick={() => navigate(`/growth/quotations/${String(r.id)}`)}
                        >
                          Open
                        </Button>
                      ),
                    },
                  ]}
                />
              </Card>
            ),
          },
        ]}
      />
    </div>
  );
};

export default QuotationConversionReportsPage;
